<template>
  <GcBaseLayout
    :title="'Production Schedule'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="false"
    :backButtonFunction="undefined"
    :formsConfig="formsConfig"
    :tabs="getBranchTabs"
    :selectedIndex="selectedTabIndex"
    ref="gc-base-layout"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Production Schedule'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Production Schedule'"
        :secondaryLabel="undefined"
      />
    </template>

    <div
      class="gc-syncfusion production-calendar"
      v-bind:class="{ 'is-linked-sales-orders': SelectedScheduleTab === 1 }"
    >
      <div class="main-content" v-if="loaded">
        <div>
          <DxTabs
            :selected-index="SelectedProcessTab"
            :items="getTimeLineTabs"
            :orientation="'horizontal'"
            :styling-mode="'secondary'"
            :keyExpr="'id'"
            :element-attr="{
              class: 'process-tabs',
            }"
            :scrollByContent="true"
            :showNavButtons="true"
          />
        </div>
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer && !IsCapacityView"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <div v-if="!IsCapacityView">
              <DxTabs
                :selected-index="SelectedScheduleTab"
                :items="[
                  {
                    id: 0,
                    text: 'Work Orders',
                  },
                  {
                    id: 1,
                    text: 'Linked Sales Orders',
                    disabled: IsTimeLineView,
                  },
                ]"
                :orientation="'horizontal'"
                :styling-mode="'secondary'"
                :width="'auto'"
                :keyExpr="'id'"
                @itemClick="onScheduleTabChanged"
              />
            </div>
            <div class="button-toolbar" v-if="!IsCapacityView">
              <div>
                <DxDropDownButton
                  text="Add New"
                  type="default"
                  styling-mode="contained"
                  class="button-full-width margin-right-5 override-styles"
                  icon="plus"
                  width="100"
                  :visible="SelectedScheduleTab === 0 && !IsCapacityView"
                  :onItemClick="addNewWorkOrder"
                  :items="getAddNewItems"
                  keyExpr="value"
                  displayExpr="text"
                  :drop-down-options="{ width: 150 }"
                />
              </div>
              <div>
                <DxButton
                  type="normal"
                  styling-mode="contained"
                  class="button-full-width override-styles"
                  icon="filter"
                  width="30"
                  v-if="!showRightContainer"
                  @click="openFilterPanel()"
                />
              </div>
            </div>

            <CapacityToolbar
              v-if="IsCapacityView"
              :current-date="selectedDate"
              :current-view="currentView"
              @navigate="onCapacityNavigate"
              @set-view="onCapacitySetView"
              @go-today="onCapacityGoToday"
              @date-selected="onCapacityDateSelected"
            />
            <ejs-schedule
              v-if="!IsCapacityView"
              :id="'ejs-schedule'"
              :height="height"
              :width="width"
              :views="views"
              :showQuickInfo="isTablet"
              :selectedDate="selectedDate"
              :eventSettings="getEventSettings"
              :popupOpen="onPopupOpen"
              :eventClick="onEventClick"
              :dragStart="onDragStart"
              :dragStop="onDragStop"
              :resizeStart="onResizeStart"
              :resizeStop="onResizeStop"
              ref="scheduleObj"
              :timeScale="{
                enable: SelectedScheduleTab === 0 && !IsCapacityView,
              }"
              :eventRendered="onEventRendered"
              :allowDragAndDrop="SelectedScheduleTab === 0"
              :allowResizing="
                SelectedScheduleTab === 0 && getPermissionToCreate
              "
              @navigating="onNavigating"
              :workHours="{ highlight: true, start: '00:00', end: '00:00' }"
              :showAllDayRow="false"
              :actionComplete="onActionComplete"
              :currentView="IsTimeLineView ? 'TimelineDay' : 'Day'"
              rowAutoHeight="true"
            >
              <e-views>
                <!-- Day View Tiles -->
                <e-view
                  option="Day"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'dayEventTemplate'"
                  v-if="!IsTimeLineView"
                >
                  <template v-slot:dayEventTemplate="{ data }">
                    <CalendarDayTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />

                    <SODayTile :data="data" v-else />
                  </template>
                </e-view>

                <!-- Time Line Day Tiles -->
                <e-view
                  option="TimelineDay"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'dayEventTemplate'"
                  :timeScale="{
                    enable: !IsCapacityView,
                    interval: 30,
                  }"
                  v-if="IsTimeLineView"
                >
                  <template v-slot:dayEventTemplate="{ data }">
                    <CalendarDayLineTile :data="data" />
                  </template>
                </e-view>

                <!-- Time Line Week Tiles -->
                <e-view
                  option="TimelineWeek"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'dayEventTemplate'"
                  v-if="IsTimeLineView"
                  :timeScale="{
                    enable: !IsCapacityView,
                    interval: 30,
                  }"
                >
                  <template v-slot:dayEventTemplate="{ data }">
                    <CalendarDayLineTile :data="data" /> </template
                ></e-view>

                <!-- Time Line Work Week Tiles -->
                <e-view
                  option="TimelineWorkWeek"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'dayEventTemplate'"
                  v-if="IsCapacityView"
                  :timeScale="{
                    enable: !IsCapacityView,
                    interval: 30,
                  }"
                ></e-view>

                <!-- Time Line Month -->
                <e-views
                  v-if="IsCapacityView"
                  option="TimelineMonth"
                  dateFormat="dd-MMM-yyyy"
                  :timeScale="{
                    enable: !IsCapacityView,
                    interval: 30,
                  }"
                >
                </e-views>

                <!-- Week View Tile -->
                <e-view
                  option="Week"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'weekEventTemplate'"
                  v-if="!IsTimeLineView"
                >
                  <template v-slot:weekEventTemplate="{ data }">
                    <CalendarWeekTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />
                    <SOWeekTile :data="data" v-else />
                  </template>
                </e-view>

                <!-- Work Week View Tile -->
                <e-view
                  option="WorkWeek"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'workEventTemplate'"
                  v-if="!IsTimeLineView"
                >
                  <template v-slot:workEventTemplate="{ data }">
                    <CalendarWeekTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />
                    <SOWeekTile :data="data" v-else />
                  </template>
                </e-view>

                <!-- Month View Tile -->
                <e-view
                  option="Month"
                  :eventTemplate="'monthEventTemplate'"
                  v-if="!IsTimeLineView"
                >
                  <template v-slot:monthEventTemplate="{ data }">
                    <CalendarMonthTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />
                    <SOMonthTile :data="data" v-else />
                  </template>
                </e-view>
              </e-views>
              <!-- main tile template  -->

              <e-resources>
                <e-resource
                  field="ResourceId"
                  title="Process Type"
                  name="ProcessTypes"
                  :dataSource="getProcessTabs"
                  textField="text"
                  idField="code"
                >
                </e-resource>
              </e-resources>

              <!-- tooltip template  -->
              <template v-slot:tooltipTemplate="{ data }">
                <NotesToolTip
                  v-if="data.WOType === 'Note' && SelectedScheduleTab === 0"
                  :data="data"
                />
                <WOToolTip :data="data" v-else-if="SelectedScheduleTab === 0" />
                <SOToolTip :data="data" v-else />
              </template>
            </ejs-schedule>

            <!-- Capacity heatmap -->
            <CapacityHeatmap
              v-if="IsCapacityView && schedulerReady"
              :branch-code="BranchCode"
              :current-date="selectedDate"
              :current-view="currentView"
              :processes="getProcessTabs"
              @navigate-to-schedule="onNavigateToSchedule"
              :show-loading="showLoading"
              :max-height="`${height}px`"
            />
          </template>
          <template #rightContent>
            <gc-filter-panel
              :toolbarVisible="true"
              :toolbar="filterToolbar"
              :formItemData="filterFormItems"
              :setGridPanelFilter="setGridPanelFilter"
              :gridFoolter="false"
              :showLoading="showLoading"
              :from="'transaction-screen'"
              :isNormal="false"
              :persistenceKey="undefined"
              :overWriteClear="true"
              :ref="'gc-filter-panel'"
              :heightAdjustment="36"
              :key="filterPanelIndex"
            >
            </gc-filter-panel>
          </template>
        </gc-filter-split-view>
      </div>

      <GcPopUpWorkOrder
        :popupConfig="popupConfig"
        :on-hide="onPopupHide"
        :wrapper-attr="{
          class: 'css-v2 gc-popup gc-popup-form gc-popup-work-order',
        }"
        :data-model="dataModel"
        :show-loading="showLoading"
        :width="1024"
        :height="370"
        :save-call-back="onWorkOrderSave"
        :displayServerError="displayServerError"
        v-if="popupConfig.isPopupShow && !IsMaintenanceMode"
      />

      <GcPopupMaintenanceOrder
        :popupConfig="popupConfig"
        :on-hide="onPopupHide"
        :wrapper-attr="{
          class: 'css-v2 gc-popup gc-popup-form gc-popup-maintenance-order',
        }"
        :data-model="dataModel"
        :show-loading="showLoading"
        :width="1024"
        :height="370"
        :save-call-back="onWorkOrderSave"
        :displayServerError="displayServerError"
        v-if="popupConfig.isPopupShow && IsMaintenanceMode"
      />

      <GcPopupScheduleNote
        :popupConfig="notePopupConfig"
        :data-model="dataModel"
        :on-hide="onScheduleNotePopupHide"
        v-if="notePopupConfig.isPopupShow && IsScheduleNoteMode"
        :displayServerError="displayServerError"
      />
      <!-- @setIsVisible="onScheduleNotePopupVisibleChange" -->

      <GcPopupWoSoSequencing
        :popupConfig="sequencePopupConfig"
        :on-hide="onPopupHide"
        :wrapper-attr="{
          class: 'css-v2 gc-popup gc-popup-form gc-popup-wo-so-sequencing',
        }"
        :data-model="dataModel"
        :show-loading="showLoading"
        :width="1024"
        :height="370"
        :save-call-back="onWorkOrderSave"
        :displayServerError="displayServerError"
        v-if="
          sequencePopupConfig.isPopupShow &&
          !IsMaintenanceMode &&
          SelectedScheduleTab === 1
        "
        @showWorkOrderEdit="onEditWorkOrderHandler"
        @hideSequencePopup="onHideSequencePopupHandler"
      />
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import {
  Day,
  Week,
  WorkWeek,
  Month,
  Agenda,
  DragAndDrop,
  ViewDirective,
  ViewsDirective,
  TimelineViews,
  ResourceDirective,
  ResourcesDirective,
  Resize,
} from "@syncfusion/ej2-vue-schedule";

import { ProductionCalendarService } from "../../../_services/api/production/production-calendar.service";
import { ProductionCalendarLinkedSOService } from "../../../_services/api/production/production-calendar-linked-so";
import { getCalendarFilterValues } from "../../../_helpers/calendar/filter-panel";

import { DataManager, ODataV4Adaptor } from "@syncfusion/ej2-data";
import { FeatureStorage, TokenStorage } from "../../../_services/storage";
import appConfig from "../../../app-config";
import { WorkOrderService } from "@/_services/api/production/work-order.service";
import { formatNumber } from "devextreme/localization";
import CalendarDayTile from "../../../components/production/work-order/calendar-day-tile.vue";
import CalendarDayLineTile from "../../../components/production/work-order/calendar-day-line-tile.vue";
import CalendarWeekTile from "../../../components/production/work-order/calendar-week-tile.vue";
import CalendarMonthTile from "../../../components/production/work-order/calendar-month-tile.vue";
import WOToolTip from "../../../components/production/work-order/wo-tool-tip.vue";
import NotesToolTip from "../../../components/production/work-order/notes-tool-tip.vue";

// Tiles for Linked SOs
import SODayTile from "../../../components/production/linked-sales-orders/so-day-tile.vue";
import SOWeekTile from "../../../components/production/linked-sales-orders/so-week-tile.vue";
import SOMonthTile from "../../../components/production/linked-sales-orders/so-month-tile.vue";
import SOToolTip from "../../../components/production/linked-sales-orders/so-tool-tip.vue";
import { BranchService } from "../../../_services/api/sales/branch.service";
import SettingsService from "../../../_services/api/settings/graycorp/settings.service";
import sortProcesses from "../../../_helpers/calendar/scheduler-process";

import GcPopupMaintenanceOrder from "../../../popups/maintenance-order/gc-popup-maintenance-order";
import GcPopupScheduleNote from "../../../popups/production/gc-popup-schedule-note.vue";
import GcPopupWoSoSequencing from "../../../popups/work-order/gc-popup-wo-so-sequencing.vue";
import CapacityToolbar from "../../../components/production/capacity-heatmap/CapacityToolbar.vue";
import CapacityHeatmap from "../../../components/production/capacity-heatmap/CapacityHeatmap.vue";

function getRandomHexColor() {
  const randomColor = Math.floor(Math.random() * 0xffffff).toString(16);
  return `#${randomColor.padStart(6, "0")}`;
}

function normalizeEvents(events) {
  const normalized = [];
  events.forEach((ev) => {
    const start = new Date(ev.startTime);
    const end = new Date(ev.endTime);
    const diff = end.getTime() - start.getTime();

    // exactly 24h (or 1 day in ms)
    if (diff === 24 * 60 * 60 * 1000) {
      // adjust EndTime back to 23:59:59 of same day
      const fixedEnd = new Date(start);
      fixedEnd.setHours(23, 59, 59, 999);

      normalized.push({
        ...ev,
        endTime: fixedEnd,
        IsAllDay: false, // make sure it's forced into time scale
      });
    } else {
      const isMidnight =
        end.getHours() === 0 &&
        end.getMinutes() === 0 &&
        end.getSeconds() === 0;

      // Adjust end date if it's exactly midnight
      const adjustedEnd = isMidnight ? new Date(end.getTime() - 1) : end;

      const startDate = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
      );
      const endDate = new Date(
        adjustedEnd.getFullYear(),
        adjustedEnd.getMonth(),
        adjustedEnd.getDate(),
      );

      const dayCount =
        Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      if (dayCount > 1) {
        for (let i = 0; i < dayCount; i++) {
          const currentDay = new Date(startDate);
          currentDay.setDate(startDate.getDate() + i);

          let segmentStart = new Date(currentDay);
          let segmentEnd = new Date(currentDay);

          if (i === 0) {
            segmentStart = start;
            segmentEnd.setHours(23, 59, 59, 999);
          } else if (i === dayCount - 1) {
            segmentStart.setHours(0, 0, 0, 0);
            segmentEnd = adjustedEnd;
          } else {
            segmentStart.setHours(0, 0, 0, 0);
            segmentEnd.setHours(23, 59, 59, 999);
          }

          normalized.push({
            ...ev,
            startTime: segmentStart,
            endTime: segmentEnd,
            IsAllDay: false,
          });
        }
      } else {
        normalized.push(ev);
      }
    }
  });

  return normalized;
}

class WorkOrderAdaptor extends ODataV4Adaptor {
  processResponse() {
    let original = super.processResponse.apply(this, arguments);

    const scheduleElement = document.getElementById("ejs-schedule");
    const currentView =
      scheduleElement && scheduleElement.ej2_instances[0]
        ? scheduleElement.ej2_instances[0].currentView
        : "Unknown";

    original.forEach((item) => {
      item.subject = item.WorkOrderCode;
      item.startTime = new Date(item.StartDate.toISOString().replace("Z", ""));
      item.endTime =
        currentView == "Month"
          ? new Date(item.StartDate.toISOString().replace("Z", ""))
          : new Date(item.EndDate.toISOString().replace("Z", ""));
      item.IsAllDay = false;
      item.backGround = getRandomHexColor();
      item.IsBlock =
        item.WOType !== "Production" &&
        item.WOType !== "Maintenance" &&
        item.WOType !== "Note";
      item.ResourceId = item.ProcessCode;
    });

    original = normalizeEvents(original);

    return original;
  }
}
class LinkSOAdaptor extends ODataV4Adaptor {
  processResponse() {
    let original = super.processResponse.apply(this, arguments);

    original.forEach((item, index) => {
      // Convert to a fixed-time string, preserving the original timezone
      let dateObj = new Date(item.SalesOrderShipDate);
      item.SalesOrderShipDate = dateObj.toISOString().replace("Z", "");

      item.id = index + 1;
      item.subject = item.WorkOrderCode;
      item.startTime = new Date(item.PackDate).toFilterDateStartTime();
      item.endTime = new Date(item.PackDate).toFilterDateEndTime();
      item.IsAllDay = true;
      item.backGround = getRandomHexColor();

      if (!item.TransportStatusHexColour)
        item.TransportStatusHexColour = "#800080";
    });

    return original;
  }
}

let draggedEvent = null;
// let draggedElement = null;
let refreshLayout = null;
let resizeEvent = null;

export default {
  mixins: [RouteMixin, FormMixin],
  provide: {
    schedule: [
      Day,
      Week,
      WorkWeek,
      Month,
      Agenda,
      DragAndDrop,
      TimelineViews,
      Resize,
    ],
  },
  components: {
    "e-views": ViewsDirective,
    "e-view": ViewDirective,
    CalendarDayTile,
    CalendarWeekTile,
    CalendarMonthTile,
    WOToolTip,
    SODayTile,
    SOWeekTile,
    SOMonthTile,
    SOToolTip,
    GcPopupMaintenanceOrder,
    "e-resource": ResourceDirective,
    "e-resources": ResourcesDirective,
    CalendarDayLineTile,
    NotesToolTip,
    GcPopupScheduleNote: GcPopupScheduleNote,
    GcPopupWoSoSequencing,
    CapacityToolbar,
    CapacityHeatmap,
  },
  data() {
    return {
      tabInfo: [],
      selectedIndex: 0,
      showLeftContainer: true,
      showRightContainer: true,
      loaded: false,
      height: "auto",
      width: "100%",
      views: ["Day", "Week", "WorkWeek", "Month"],
      selectedDate: new Date(),
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
            this.$nextTick(() => {
              this.rePaintSchedule();

              this.$nextTick(() => {
                this.fetchWorkHours();
              });
            });
          },
        },
      },
      filterValues: false,
      ProcessCode: undefined,
      isFetchingWorkHours: false,
      notePopupConfig: {
        title: "",
        secondaryTitle: undefined,
        isFormLoading: false,
        popupKey: 0,
        formKey: 0,
        height: 370,
        isPopupShow: false,
        hideOnOutsideClick: false,
        formMode: "edit",
        isEditable: false,
      },
      popupConfig: {
        title: "",
        secondaryTitle: undefined,
        isFormLoading: false,
        popupKey: 0,
        formKey: 0,
        height: 370,
        isPopupShow: false,
        hideOnOutsideClick: false,
        toolbarMenuItems: [
          {
            menuText: "Copy",
            visible: false, // pending discussions #54331
          },
          { menuText: "Cancel", onClick: this.onCancelWorkOrderHandler },
          { menuText: "Reopen", onClick: this.onReopenWorkOrderHandler },
          { menuText: "Complete", onClick: this.onCompleteWorkOrderHandler },
        ],
        formMode: "create",
        isEditable: false,
        calendarDate: undefined,
        workOrderCode: undefined,
      },
      sequencePopupConfig: {
        title: "",
        secondaryTitle: undefined,
        isFormLoading: false,
        popupKey: 0,
        formKey: 0,
        height: 370,
        isPopupShow: false,
        hideOnOutsideClick: false,
        formMode: "review",
        isEditable: false,
        calendarDate: undefined,
        workOrderCode: undefined,
      },
      dataModel: undefined,
      SelectedScheduleTab: 0,
      SelectedProcessTab: 0,
      SelectedTimelineTab: -1,
      currentView: undefined,
      branchTabs: [],
      processTabs: [],
      BranchCode: undefined,
      filterPanelIndex: 0,
      IsMaintenanceMode: false,
      schedulerReady: false,
      IsTimeLineView: true,
      isScheduleNotePopupVisible: false,
      IsCapacityView: false,
    };
  },
  async beforeCreate() {
    await BranchService.getManufactureBranch().then((res) => {
      res.data.value.forEach((element) => {
        this.branchTabs.push({
          BranchCode: element.Code,
          text: element.Code + " - " + element.Description,
          path: `/production-calendar/` + element.Code,
          applicationFeatureCode: "ProductionSchedule",
          onClick: this.onBranchChanged,
        });
      });
    });

    let processTabs = [];

    await ProductionCalendarService.getProductionProcess().then((res) => {
      res.data.value.forEach((element, index) => {
        processTabs.push({
          id: index,
          code: element.Code,
          text: element.Description,
          applicationFeatureCode: "ProductionSchedule",
          onClick: this.onProcessChanged,
        });
      });
    });

    let definedSortOrder = [];
    let defaultSortOrder = undefined;

    let PSDefinedSortOrder = await SettingsService.getBySettingName(
      "ProductionScheduleDefinedSortOrder",
    );

    if (PSDefinedSortOrder && PSDefinedSortOrder.data.value.length > 0) {
      definedSortOrder = PSDefinedSortOrder.data.value[0].SettingValue.split(
        ",",
      ).map((item) => item.trim());
    }

    let PSDefaulSortOrder = await SettingsService.getBySettingName(
      "ProductionScheduleDefaulSortOrder",
    );

    if (PSDefaulSortOrder && PSDefaulSortOrder.data.value.length > 0) {
      defaultSortOrder = PSDefaulSortOrder.data.value[0].SettingValue;
    }

    this.processTabs = sortProcesses(
      processTabs,
      definedSortOrder,
      defaultSortOrder,
    );

    // this.ProcessCode = this.processTabs[0].code;
    this.IsTimeLineView = true;
    this.applyGroupByView();

    this.SelectedProcessTab = 1;

    this.$nextTick(() => {
      this.currentView = "TimelineDay";
      this.fetchWorkHours();
      setTimeout(() => {
        this.SelectedProcessTab = 1;
      }, 100);
    });

    if (this.baseView.id) {
      let index = this.branchTabs.findIndex(
        (x) => x.BranchCode === this.baseView.id,
      );
      if (index !== -1) this.selectedIndex = index;
      this.BranchCode = this.baseView.id;
    } else {
      this.BranchCode = this.branchTabs[0].BranchCode;
    }
  },
  mounted() {
    setTimeout(() => {
      var scrollableView = document.getElementsByClassName(
        "layout-body dx-scrollview",
      );

      this.height = scrollableView[0].clientHeight - 65 - 74;
    }, 250);
  },
  async created() {
    this.loaded = true;

    this.$nextTick(() => {
      this.fetchWorkHours();
    });
  },
  methods: {
    async fetchWorkHours(autoScroll = true) {
      if (this.IsCapacityView) {
        this.applyGroupByView();
        return;
      }

      if (
        !document.getElementById("ejs-schedule") ||
        !document.getElementById("ejs-schedule").ej2_instances ||
        document.getElementById("ejs-schedule").ej2_instances.length === 0
      )
        return;

      if (
        (!this.ProcessCode || this.SelectedScheduleTab !== 0) &&
        !this.IsTimeLineView &&
        !this.IsCapacityView
      )
        return;

      // Prevent concurrent fetchWorkHours calls
      if (this.isFetchingWorkHours) {
        return;
      }

      this.isFetchingWorkHours = true;
      this.showLoading(true);
      const scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];
      let earliestStart = null;

      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Get the visible date range of the current view
      const visibleDates = scheduleObj.getCurrentViewDates();
      if (visibleDates.length === 0) {
        return;
      }
      // if (visibleDates.length === 0) {
      //   setTimeout(() => {
      //     this.fetchWorkHours(autoScroll);
      //   }, 250);
      //   return;
      // }
      const startDate = visibleDates[0];
      const endDate = visibleDates[visibleDates.length - 1];

      this.schedulerReady = true;

      try {
        const response = await ProductionCalendarService.getWorkHours(
          new Date(startDate).toFilterDateString(),
          new Date(endDate).toFilterDateString(),
          this.IsTimeLineView ? undefined : this.ProcessCode,
        );

        if (!scheduleObj) return;

        if (
          response &&
          response.data &&
          response.data.value &&
          response.data.value.length > 0
        ) {
          scheduleObj.workHours.highlight = true;
          // await this.rePaintSchedule();
          const workHours = response.data.value;

          await this.$nextTick();

          workHours.forEach((wh, index) => {
            // Dev only, remove timezone for dev purposes
            // wh.DateStart = wh.DateStart.replace("Z", "");
            // wh.DateEnd = wh.DateEnd.replace("Z", "");
            wh.ShiftDateStart = wh.ShiftDateStart.replace("Z", "");

            const start = new Date(wh.DateStart.replace("Z", ""));
            const end = new Date(wh.DateEnd.replace("Z", ""));

            const shiftDate = this.normalizeDate(wh.ShiftDateStart);

            // track earliest start time
            if (
              (!earliestStart || start < earliestStart) &&
              !(
                start.getHours() === 0 &&
                start.getMinutes() === 0 &&
                start.getSeconds() === 0
              )
            ) {
              earliestStart = start;
            }

            // Get groupIndex for the current resource
            let groupIndex = this.getProcessTabs.findIndex(
              (r) => r.code === wh.ProcessCode,
            );

            setTimeout(() => {
              if (!scheduleObj || this.IsCapacityView) return;
              if (
                !scheduleObj.resourceBase.lastResourceLevel ||
                (this.currentView !== "TimelineDay" &&
                  this.currentView !== "TimelineWeek")
              )
                groupIndex = undefined;

              if (start.toDateString() === end.toDateString()) {
                // Normal case: same day

                if (groupIndex !== -1 && groupIndex !== undefined) {
                  scheduleObj.setWorkHours(
                    [shiftDate],
                    this.formatTime(start),
                    this.formatTime(end),
                    groupIndex,
                  );
                } else {
                  scheduleObj.setWorkHours(
                    [shiftDate],
                    this.formatTime(start),
                    this.formatTime(end),
                  );
                }
              } else {
                // Cross-midnight case → split highlight

                // 1) Late evening on shift start day
                if (groupIndex !== -1 && groupIndex !== undefined) {
                  scheduleObj.setWorkHours(
                    [shiftDate],
                    this.formatTime(start),
                    "23:59",
                    groupIndex,
                  );
                } else {
                  scheduleObj.setWorkHours(
                    [shiftDate],
                    this.formatTime(start),
                    "23:59",
                  );
                }

                // 2) Early morning on next day
                const nextDay = new Date(shiftDate);
                nextDay.setDate(nextDay.getDate() + 1);

                if (groupIndex !== -1 && groupIndex !== undefined) {
                  scheduleObj.setWorkHours(
                    [nextDay],
                    "00:00",
                    this.formatTime(end),
                    groupIndex,
                  );
                } else {
                  scheduleObj.setWorkHours(
                    [nextDay],
                    "00:00",
                    this.formatTime(end),
                  );
                }
              }
            }, index * 100);
          });
        } else {
          scheduleObj.workHours.highlight = false;
          // No work hours → reset scroll to top (00:00)
          scheduleObj.scrollTo("00:00");
        }
      } catch (error) {
        this.displayServerError(error);
      } finally {
        scheduleObj.currentView = this.currentView;
        await this.$nextTick(() => {
          // after processing all work hours, scroll
          if (earliestStart && autoScroll) {
            this.scrollToWorkHoursStart(earliestStart);
          }
        });

        setTimeout(() => {
          scheduleObj.refreshLayout();
          this.showLoading(false);
          this.isFetchingWorkHours = false;
        }, 500);
      }
    },

    onActionComplete(args) {
      // Force refresh after group change
      if (
        args.requestType === "viewNavigate" ||
        args.requestType === "viewChange"
      ) {
        this.applyGroupByView();
      }
    },

    applyGroupByView() {
      if (!this.$refs.scheduleObj || !this.$refs.scheduleObj.ej2Instances)
        return;

      const scheduleObj = this.$refs.scheduleObj.ej2Instances;
      if (!scheduleObj) return;

      scheduleObj.scrollTo("00:00");
      const activeView = scheduleObj.currentView;

      // Apply grouping for TimelineDay, TimelineWeek, or Capacity view
      if (
        activeView === "TimelineDay" ||
        activeView === "TimelineWeek" ||
        this.IsCapacityView
      ) {
        scheduleObj.group = { resources: ["ProcessTypes"] };
      } else {
        scheduleObj.group = { resources: [] };
      }

      // Re-render layout to reflect changes
      scheduleObj.refreshLayout();
    },
    formatTime(isoString) {
      const date = new Date(isoString); // converts UTC string to local time
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    normalizeDate(isoString) {
      const d = new Date(isoString);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()); // strip time
    },

    onScheduleTabChanged(e) {
      // Work Orders
      if (e.itemIndex === 0) {
        this.SelectedScheduleTab = 0;
        this.$nextTick(() => {
          this.rePaintSchedule();

          this.$nextTick(() => {
            this.fetchWorkHours();
          });
        });
      }
      // Linked Sales Orders
      else if (e.itemIndex === 1) {
        this.SelectedScheduleTab = 1;
        this.$nextTick(() => {
          this.rePaintSchedule();

          this.$nextTick(() => {
            this.fetchWorkHours();
          });
        });
      }
    },
    rePaintSchedule() {
      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      scheduleObj.refresh();
    },
    getFormattedQty(value) {
      return formatNumber(value, "##,###,###,###,##0.00");
    },
    /**
     * Open Filter Panel
     */
    openFilterPanel() {
      this.showRightContainer = true;
      this.$nextTick(() => {
        this.rePaintSchedule();

        this.$nextTick(() => {
          this.fetchWorkHours();
        });
      });
    },
    /**
     * Filter Panel values
     * @param e
     * @param valueChanged
     */
    setGridPanelFilter(e, valueChanged, filterType = "Apply") {
      if (valueChanged && filterType.toLowerCase() === "refresh") {
        this.onWorkOrderSave();
      } else {
        this.filterValues = getCalendarFilterValues(e, valueChanged);
      }
    },
    onBranchChanged(e) {
      if (e && e.BranchCode !== undefined && this.BranchCode !== e.BranchCode) {
        this.BranchCode = e.BranchCode;

        if (this.IsCapacityView) {
          // Capacity heatmap will refetch via branchCode watcher
          return;
        }

        this.schedulerReady = false;
        const schedulerElement = document.getElementById("ejs-schedule");
        if (schedulerElement) {
          this.showLoading(true);
          const scheduleObj = schedulerElement.ej2_instances[0];

          if (scheduleObj) {
            this.filterValues = undefined;
            this.$refs["gc-filter-panel"].$refs["gc-form"].clearFormModel();
            scheduleObj.refreshLayout();
            this.$nextTick(() => {
              setTimeout(() => {
                this.fetchWorkHours();
                this.showLoading(false);
              }, 500);
            });
          } else this.showLoading(false);
        }
      }
    },
    /**
     * dynamic tab value changed - handles Capacity, Timeline, and Process tabs
     */
    onTimelineTabChanged(e) {
      if (this.IsCapacityView) return;
      let item = e.itemData;
      this.schedulerReady = false;

      if (item && item.code === "capacity") {
        this.SelectedProcessTab = 0;
        this.showLoading(true);
        this.IsCapacityView = true;
        this.SelectedScheduleTab = 0;
        this.currentView = "TimelineWeek";
        this.ProcessCode = undefined;
        this.IsTimeLineView = true;

        if (
          !document.getElementById("ejs-schedule") ||
          !document.getElementById("ejs-schedule").ej2_instances
        ) {
          this.showLoading(false);
          return;
        }
        let scheduleObj =
          document.getElementById("ejs-schedule").ej2_instances[0];

        if (scheduleObj) {
          this.filterValues = undefined;
          this.$refs["gc-filter-panel"].$refs["gc-form"].clearFormModel();
          scheduleObj.currentView = this.currentView;
          scheduleObj.refreshLayout();

          this.$nextTick(() => {
            this.applyGroupByView();
          });

          setTimeout(() => {
            this.schedulerReady = true;
            this.showLoading(false);
          }, 500);
        } else {
          this.showLoading(false);
        }
        return;
      }
    },

    /**
     * dynamic tab value changed
     */
    async onProcessChanged(e, date) {
      this.IsCapacityView = false;

      this.$nextTick(async () => {
        if (
          !document.getElementById("ejs-schedule") ||
          !document.getElementById("ejs-schedule").ej2_instances
        ) {
          return;
        }

        let item = e.itemData;
        this.schedulerReady = false;
        if (item && item.code !== undefined && this.ProcessCode !== item.code) {
          this.showLoading(true);
          let scheduleObj =
            document.getElementById("ejs-schedule").ej2_instances[0];

          if (scheduleObj) {
            this.filterValues = undefined;
            this.$refs["gc-filter-panel"].$refs["gc-form"].clearFormModel();
            //   scheduleObj.refresh();
            this.$nextTick(async () => {
              if (item.code === "timeline") {
                this.SelectedScheduleTab = 0;
                this.currentView = "TimelineDay";
                this.ProcessCode = undefined;
                this.IsTimeLineView = true;
                this.schedulerReady = true;

                setTimeout(() => {
                  scheduleObj.scrollToResource(0);
                }, 100);
              } else {
                if (this.currentView !== "Week") {
                  this.currentView = "Week";
                }
                this.ProcessCode = item.code;
                this.IsTimeLineView = false;
                this.schedulerReady = true;
              }
              // await scheduleObj.refreshLayout();

              setTimeout(async () => {
                if (date) this.selectedDate = date;
                // scheduleObj.refreshEvents(true);
                this.$nextTick(async () => {
                  await this.applyGroupByView();
                  setTimeout(async () => {
                    scheduleObj.currentView = this.currentView;
                    setTimeout(async () => {
                      await this.fetchWorkHours();
                      this.showLoading(false);
                    }, 500);
                  }, 100);
                });
              }, 100);
            });
          } else {
            this.showLoading(false);
          }
        }
      });
    },

    /**
     * Handle navigation from capacity heatmap back to timeline schedule
     * @param {Object} payload - { processCode, date }
     */
    onNavigateToSchedule({ date }) {
      this.IsCapacityView = false;

      this.schedulerReady = false;

      this.$nextTick(async () => {
        // Clear filters when switching from Capacity to Timeline
        this.filterValues = undefined;
        if (this.$refs["gc-filter-panel"]) {
          this.$refs["gc-filter-panel"].$refs["gc-form"].clearFormModel();
        }

        // // Set Timeline tab as selected
        const timelineTabIndex = this.getTimeLineTabs.findIndex(
          (t) => t.code === "timeline",
        );
        if (timelineTabIndex !== -1) {
          this.SelectedProcessTab += 1;
        } else return;

        const itemData = this.getTimeLineTabs.find(
          (t) => t.code === "timeline",
        );

        this.$nextTick(() => {
          this.onProcessChanged(
            {
              itemData: itemData,
            },
            date,
          );
        });
      });
    },

    onCapacityNavigate(direction) {
      const d = new Date(this.selectedDate);
      if (this.currentView === "TimelineMonth") {
        d.setMonth(d.getMonth() + direction);
      } else if (this.currentView === "TimelineDay") {
        d.setDate(d.getDate() + direction); // Day view: advance by 1 day
      } else {
        d.setDate(d.getDate() + direction * 7); // Week/WorkWeek: advance by 7 days
      }
      this.selectedDate = d;
    },

    onCapacitySetView(view) {
      this.currentView = view;
    },

    onCapacityGoToday() {
      this.selectedDate = new Date();
    },

    onCapacityDateSelected(date) {
      this.selectedDate = new Date(date);
    },

    /**
     * on default popup open event
     * @param args
     */
    async onPopupOpen(args) {
      if (args && args.data && args.data.WOType === "Note") {
        args.cancel = true;
        return;
      }

      if (args.type === "Editor" && this.SelectedScheduleTab === 0) {
        args.cancel = true;

        if (this.IsScheduleNoteMode) {
          this.notePopupConfig.isPopupShow = true;
          this.notePopupConfig.formMode = "create";
          this.notePopupConfig.title = "Add New Schedule Note";
          // this.onScheduleNotePopupVisibleChange(true);
        } else {
          this.popupConfig.title = this.IsMaintenanceMode
            ? "Create Maintenance Order"
            : "Create Work Order";
          this.popupConfig.hideOnOutsideClick = false;
          this.popupConfig.workOrderCode = undefined;
          this.popupConfig.formMode = "create";
          await this.showEventCreateOrEditModal(args, args.data);
        }
      }

      if (args.type === "Editor" && this.SelectedScheduleTab === 1) {
        args.cancel = true;
      }
    },

    async showScheduleNoteModal(data) {
      // this.popupConfig.formMode = "edit";
      // this.popupConfig.hideOnOutsideClick = true;
      // this.popupConfig.workOrderCode = data.WorkOrderCode;
      // this.popupConfig.title = this.IsMaintenanceMode
      //   ? `Maintenance Order #${data.WorkOrderCode}`
      //   : `Work Order #${data.WorkOrderCode}`;
      // this.popupConfig.secondaryTitle =
      //   data.ProductCode + " - " + data.ProductDescription1;
      // this.popupConfig.isEditable =
      //   (data.FriendlyStatus === "Active" ||
      //     data.FriendlyStatus === "In Production") &&
      //   FeatureStorage.checkPermission(
      //     this.IsMaintenanceMode ? "MaintenanceOrder" : "WorkOrder",
      //     "edit"
      //   );

      const response = await ProductionCalendarService.getScheduleNote(
        data.RecordId,
      );

      let result = response.data.value[0];

      // remove timezone for dev purposes
      result.StartDateTime = result.StartDateTime.replace("Z", "");
      result.EndDateTime = result.EndDateTime.replace("Z", "");

      this.dataModel = response.data.value[0];
      this.IsScheduleNoteMode = true;
      this.notePopupConfig.title = `Schedule Note`;
      this.notePopupConfig.isPopupShow = true;
      this.notePopupConfig.formMode = "review";
    },
    onScheduleNotePopupHide(repaint = true) {
      this.notePopupConfig.isPopupShow = false;
      this.dataModel = undefined;

      if (repaint) this.rePaintSchedule();
    },
    async showWorkOrderModal(data) {
      await this.openWorkOrderPopup(data, {
        fetchFromApi: true,
      });
    },

    async showWoSoSequencingModal(data) {
      this.IsMaintenanceMode = false;

      this.sequencePopupConfig.formMode = "review";
      this.sequencePopupConfig.hideOnOutsideClick = true;
      this.sequencePopupConfig.workOrderCode = data.WorkOrderCode;

      this.sequencePopupConfig.title = `Work Order #${data.WorkOrderCode} - SO Sequencing`;
      this.sequencePopupConfig.secondaryTitle =
        data.ProductCode + " - " + data.ProductDescription1;
      this.sequencePopupConfig.isEditable =
        (data.FriendlyStatus === "Active" ||
          data.FriendlyStatus === "In Production") &&
        FeatureStorage.checkPermission("WorkOrder", "edit");

      const response = await WorkOrderService.getByCode(
        this.sequencePopupConfig.workOrderCode,
      );
      this.dataModel = response.data.value[0];
      this.sequencePopupConfig.isPopupShow = true;
      this.sequencePopupConfig.rowLineData = data;
    },

    async openWorkOrderPopup(data, options = {}) {
      const {
        fetchFromApi = false,
        triggerEdit = false,
        repaintSchedule = false,
        hideSequencePopup = false,
      } = options;

      if (hideSequencePopup) {
        this.sequencePopupConfig.isPopupShow = false;
      }

      if (repaintSchedule) {
        await this.rePaintSchedule();
      }

      // Normalize status source
      if (this.SelectedScheduleTab === 1) {
        data.FriendlyStatus = data.WorkOrderFriendlyStatus;
      }

      this.IsMaintenanceMode = data.WOType === "Maintenance";

      const isEditableStatus =
        data.FriendlyStatus === "Active" ||
        data.FriendlyStatus === "In Production";

      const usePermission = FeatureStorage.checkPermission(
        this.IsMaintenanceMode ? "MaintenanceOrder" : "WorkOrder",
        "edit",
      );

      Object.assign(this.popupConfig, {
        formMode: "review",
        hideOnOutsideClick: true,
        workOrderCode: data.WorkOrderCode,
        title: this.IsMaintenanceMode
          ? `Maintenance Order #${data.WorkOrderCode}`
          : `Work Order #${data.WorkOrderCode}`,
        secondaryTitle: `${data.ProductCode} - ${data.ProductDescription1}`,
        isEditable: usePermission && isEditableStatus,
        triggerEdit: usePermission && triggerEdit,
        displayForScheduleEditing: false,
      });

      // Only fetch when needed
      if (fetchFromApi) {
        const response = await WorkOrderService.getByCode(data.WorkOrderCode);
        this.dataModel = response.data.value[0];
      }

      this.popupConfig.isPopupShow = true;
    },
    /**
     * on event click event - by default event
     * @param args
     */
    async onEventClick(args) {
      if (args.name === "eventClick") {
        args.cancel = true;

        let data = (this.dataModel = args.event);
        if (data.WOType === "Down Time") return;

        if (data.WOType === "Note") {
          await this.showScheduleNoteModal(data);
          return;
        }

        if (this.SelectedScheduleTab === 1) {
          await this.showWoSoSequencingModal(data);
        } else {
          await this.showWorkOrderModal(data);
        }
      }
    },

    /**
     * on drag starts event
     */
    onDragStart(e, eventData) {
      // if (this.currentView === "Day" && e.e.srcElement.dataset.tooltipId)
      //    document.getElementById(
      //      e.e.srcElement.dataset.tooltipId
      //    ).style.display = "none";

      if (
        this.getPermissionToCreate == false ||
        (e.data.FriendlyStatus !== "Draft" &&
          e.data.FriendlyStatus !== "Planning" &&
          e.data.FriendlyStatus !== "Awaiting Production" &&
          e.data.FriendlyStatus !== "Review")
      )
        e.cancel = true;

      if (e.data.WOType === "Down Time") e.cancel = true;
      if (e.data.WOType === "Note") e.cancel = true;

      draggedEvent = eventData;
    },

    /**
     * on resize start event
     * @description - this function get triggers in week, workweek and month view when resizing the event between diff dates
     * @param {Object} e - The event object associated with the resize start.
     */
    onResizeStart(e) {
      if (
        this.getPermissionToCreate == false ||
        (e.data.FriendlyStatus !== "In Production" &&
          e.data.FriendlyStatus !== "Review")
      )
        e.cancel = true;

      if (e.data.WOType === "Down Time") e.cancel = true;
      if (e.data.WOType === "Note") e.cancel = true;

      resizeEvent = e && e.data ? e.data : null;
    },

    /**
     * on drag stop event - this function get triggers in week, workweek and month view.
     * between diff dates
     * @param {Object} e - The event object associated with the drag stop.
     */
    async onDragStop(e) {
      e.cancel = true;
      if (this.SelectedScheduleTab === 1) {
        const isSameDate = (date1, date2) => {
          return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
          );
        };

        if (isSameDate(e.data.PackDate, e.data.startTime)) {
          return;
        }
      }

      if (e.data) {
        // this.showLoading(true);
        var model = e.data;

        if (this.IsTimeLineView && model.ProcessCode !== model.ResourceId) {
          await this.$alert(
            "Work order cannot be moved to a different process.",
            "Warning",
            { type: "warning" },
          );

          return;
        }

        if (this.SelectedScheduleTab === 0)
          this.reOrderWorkOrder({
            ProcessCode: model.ProcessCode,
            OldScheduledDate: new Date(
              model.StartDate.toISOString().replace("Z", ""),
            ).toSaveDateTime(),
            NewScheduledDate: new Date(model.startTime).toSaveDateTime(),
            WorkOrderCode: model.WorkOrderCode,
            NewPosition: -1,
            ShiftAllocationRecordId: model.ShiftAllocationRecordId,
          });
        else {
          this.reOrderLinkSoLines({
            ProcessCode: model.ProcessCode,
            SalesOrderCode: model.SalesOrderCode,
            SalesOrderLine: model.SalesOrderLine,
            OldScheduledDate: new Date(model.PackDate).toSaveDate(),
            NewScheduledDate: new Date(model.startTime).toSaveDate(),
            WorkOrderCode: model.WorkOrderCode,
            NewPosition: -1,
          });
        }
      }
    },

    /**
     * on resize stop event
     * @description - this function get triggers in week, workweek and month view when resizing the event between diff dates
     * @param {Object} e - The event object associated with the resize stop.
     */
    async onResizeStop(e) {
      const originalStartTime = resizeEvent.startTime;
      const originalEndTime = resizeEvent.endTime;

      const newStartTime = e.data.startTime;
      const newEndTime = e.data.endTime;

      // New time should have 15 mins in between start and end time
      if ((newEndTime - newStartTime) / (1000 * 60) < 15) {
        this.$alert(
          "The duration between start and end time should be at least 15 minutes.",
          "Warning",
          { type: "warning" },
        );
      } else {
        const isStartTimeChanged =
          new Date(originalStartTime).toSaveDateTime() !==
          new Date(newStartTime).toSaveDateTime();
        const isEndTimeChanged =
          new Date(originalEndTime).toSaveDateTime() !==
          new Date(newEndTime).toSaveDateTime();

        // Proceed with updating the work order with new start and end times
        try {
          const response =
            await ProductionCalendarService.shiftAllocationResize({
              RecordID: resizeEvent.ShiftAllocationRecordId,
              NewStartDate: isStartTimeChanged
                ? new Date(newStartTime).toSaveDateTime()
                : undefined,
              NewEndDate: isEndTimeChanged
                ? new Date(newEndTime).toSaveDateTime()
                : undefined,
            });

          if (response) {
            this.onWorkOrderSave(false);

            this.$nextTick(async () => {
              await this.fetchWorkHours(false);
              this.scrollToWorkHoursStart(
                isStartTimeChanged
                  ? new Date(newStartTime)
                  : isEndTimeChanged
                  ? new Date(newEndTime)
                  : new Date(originalStartTime),
              );
            });
          }
        } catch (error) {
          // Revert tile state
          this.displayServerError(error);

          setTimeout(() => {
            this.revertResize(
              isStartTimeChanged
                ? new Date(newStartTime)
                : isEndTimeChanged
                ? new Date(newEndTime)
                : new Date(originalStartTime),
            );
          }, 500);
        }
      }
    },

    /**
     * Revert tile position after failed resize attempt
     * @param {Date} time - The time to scroll to after reverting the resize
     */
    revertResize(time) {
      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      if (scheduleObj) {
        scheduleObj.refresh();
        this.$nextTick(() => {
          this.fetchWorkHours(false);
          this.showLoading(false);

          setTimeout(() => {
            this.showLoading(false);
            this.scrollToWorkHoursStart(time);
          }, 500);
        });
      }
    },

    onEventRendered(args) {
      // args.element.style.height = "auto";
      setTimeout(() => {
        if (this.currentView === "Day") {
          let scheduleObj =
            document.getElementById("ejs-schedule").ej2_instances[0];
          const myDiv = document.querySelector(".e-content-wrap");

          if (this.hasVerticalScroll(myDiv) && !refreshLayout) {
            refreshLayout = true;
            scheduleObj.refreshLayout();
          }

          if (refreshLayout) args.element.classList.add("set-width");
        }
      }, 1 * args.data.Index);

      setTimeout(() => {
        // args.element.style.width = "100%";
      }, 100 * args.data.Index);

      if (
        args.data.IsAllDay &&
        this.currentView === "Day" &&
        this.SelectedScheduleTab === 0
      ) {
        args.element.draggable = true;
        args.element.addEventListener("dragstart", (e) =>
          this.onDragStart({ e, data: args.data }, args.data),
        );
        args.element.addEventListener("dragover", this.onDragOver);
        args.element.addEventListener("drop", (e) => this.onDrop(e, args.data));
      }
    },
    hasVerticalScroll(element) {
      return element.scrollHeight > element.clientHeight;
    },
    onDragOver(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },

    /**
     * on drag stop event - this function get triggers in day view
     * vertical drag & drop
     */
    onDrop(e, targetEventData) {
      e.preventDefault();

      if (this.SelectedScheduleTab === 0)
        this.reOrderWorkOrder({
          ProcessCode: this.ProcessCode,
          OldScheduledDate: new Date(targetEventData.startTime).toSaveDate(),
          NewScheduledDate: new Date(targetEventData.endTime).toSaveDate(),
          WorkOrderCode: draggedEvent.WorkOrderCode,
          NewPosition: targetEventData.Index + 1,
          ShiftAllocationRecordId: draggedEvent.ShiftAllocationRecordId,
        });
      else {
        this.reOrderLinkSoLines({
          ProcessCode: this.ProcessCode,
          SalesOrderCode: draggedEvent.SalesOrderCode,
          SalesOrderLine: draggedEvent.SalesOrderLine,
          OldScheduledDate: new Date(draggedEvent.PackDate).toSaveDate(),
          NewScheduledDate: new Date(targetEventData.startTime).toSaveDate(),
          WorkOrderCode: draggedEvent.WorkOrderCode,
          NewPosition: targetEventData.Index + 1,
        });
      }
    },

    async reOrderLinkSoLines(payload) {
      this.showLoading(true);
      await ProductionCalendarLinkedSOService.reOrderLinkedSalesOrder(payload)
        .then(() => {
          this.showLoading(false);
          this.onWorkOrderSave();
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err);
        });
    },
    async reOrderWorkOrder(payload) {
      this.showLoading(true);
      await WorkOrderService.reOrderWorkOrder(payload)
        .then(async () => {
          this.showLoading(false);
          this.onWorkOrderSave(false);

          this.$nextTick(async () => {
            await this.fetchWorkHours(false);
            this.scrollToWorkHoursStart(payload.NewScheduledDate);
          });
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err);
        });
    },
    onNavigating(e) {
      this.schedulerReady = false;
      if (e && e.currentView === "Month") {
        this.$nextTick(() => {
          let element = document.getElementsByClassName("e-content-wrap");
          if (element && element.length > 0) {
            element[0].style.setProperty("overflow-x", "hidden", "important");
          }
        });
      }

      if (e.action === "view") {
        this.currentView = e.currentView;
      }
      refreshLayout = false;

      this.$nextTick(() => {
        this.schedulerReady = true;
        this.fetchWorkHours();
      });
    },

    scrollToWorkHoursStart(earliestStart) {
      if (
        !document.getElementById("ejs-schedule") ||
        !document.getElementById("ejs-schedule").ej2_instances ||
        document.getElementById("ejs-schedule").ej2_instances.length === 0
      )
        return;

      const scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];
      if (!scheduleObj || !earliestStart) return;
      const scrollDate = new Date(earliestStart);
      scrollDate.setMinutes(scrollDate.getMinutes() - 30); // minus 30 mins

      const scrollTime = scrollDate.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      if (!scrollTime) return;

      scheduleObj.scrollTo(scrollTime, scrollDate);
    },
    /**
     * add new WO event
     */
    addNewWorkOrder(e) {
      if (e.itemData) {
        if (e.itemData.value === "note") {
          this.IsMaintenanceMode = false;
          this.IsScheduleNoteMode = true;
        } else if (e.itemData.value === "maintenance") {
          this.IsScheduleNoteMode = false;
          this.IsMaintenanceMode = true;
        } else {
          this.IsMaintenanceMode = false;
          this.IsScheduleNoteMode = false;
        }
      }

      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      let cellData = {
        data: {
          startTime: new Date(),
        },
        type: "Editor",
      };
      scheduleObj.popupOpen(cellData, "Editor");
    },

    async showEventCreateOrEditModal(args, eventData) {
      args.cancel = true;
      if (eventData) {
        if (this.getPermissionToCreate === false) args.cancel = true;
        else {
          this.dataModel = {
            StartDate: eventData.startTime,
          };

          this.popupConfig.calendarDate = eventData.startTime;
          this.popupConfig.isPopupShow = true;
        }
      }
    },
    /**
     * Hide A popup
     */
    onPopupHide() {
      this.IsMaintenanceMode = false;
      this.popupConfig.isPopupShow = false;
    },

    onWorkOrderSave(fetchWorkHours = true) {
      this.onPopupHide();
      this.showLoading(true);
      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      if (scheduleObj) {
        scheduleObj.refresh();
        this.$nextTick(() => {
          if (fetchWorkHours) {
            this.fetchWorkHours();
          }
          setTimeout(() => {
            this.showLoading(false);
            this.$nextTick(async () => {
              this.showEditableWorkOrderFromSheduleValidation();
            });
          }, 500);
        });
      } else {
        this.showLoading(false);
        this.$nextTick(async () => {
          this.showEditableWorkOrderFromSheduleValidation();
        });
      }
    },

    async showEditableWorkOrderFromSheduleValidation() {
      if (this.popupConfig.displayForScheduleEditing) {
        const response = await WorkOrderService.getByCode(
          this.popupConfig.workOrderCode,
        );
        this.dataModel = response.data.value[0];

        await this.openWorkOrderPopup(
          {
            ...response.data.value[0],
            ...{
              WorkOrderCode: this.dataModel.Code,
              ProductDescription1: this.dataModel.ProductDescription,
            },
          },
          {
            triggerEdit: true,
          },
        );
      }
    },

    onScheduleNotePopupVisibleChange(visible) {
      this.isScheduleNotePopupVisible = visible;
      if (!visible) {
        this.IsScheduleNoteMode = false;
      }
    },

    onHideSequencePopupHandler(repaint = false) {
      this.sequencePopupConfig.isPopupShow = false;

      if (repaint) this.rePaintSchedule();
    },

    async onEditWorkOrderHandler() {
      this.sequencePopupConfig.isPopupShow = false;

      await this.rePaintSchedule();

      const data = this.sequencePopupConfig.rowLineData;

      await this.openWorkOrderPopup(data, {
        repaintSchedule: true,
        hideSequencePopup: true,
        triggerEdit: true,
      });
    },
  },
  computed: {
    getBranchTabs() {
      return this.branchTabs;
    },
    /**
     * Return user permission for the feature
     */
    getPermissionToCreate() {
      return FeatureStorage.checkPermission("WorkOrder", "create");
    },
    /**
     * Return event settings
     */
    getEventSettings() {
      return {
        dataSource: this.getDataManager,
        fields: {
          id: "Id",
          subject: { name: "subject" },
          description: { name: "Purpose" },
          startTime: { name: "startTime" },
          endTime: { name: "endTime" },
        },
        enableTooltip: true,
        // template: "eventTemplate",
        tooltipTemplate: "tooltipTemplate",
        // enableMaxHeight: true,
        // sortComparer: (args) => {
        //   args.sort((a, b) => a.Sequence - b.Sequence);

        //   return args;
        // },
      };
    },

    /**
     * returns the datasource manager for the scheduler
     */
    getDataManager() {
      if (
        ((this.filterValues == undefined || this.ProcessCode == undefined) &&
          !this.IsTimeLineView) ||
        !this.getSchedulerReady
      ) {
        return;
      }

      let scheduleURL =
        this.SelectedScheduleTab === 0
          ? "ProductionScheduleWorkOrder"
          : "ProductionScheduleWorkOrderToSalesOrderLine";

      let filters =
        this.SelectedScheduleTab === 0
          ? `(BranchCode eq '${this.BranchCode}') and (FriendlyStatus ne 'Cancelled')&orderby=Sequence asc`
          : `(ProcessCode eq '${this.ProcessCode}') and (BranchCode eq '${this.BranchCode}') and (FriendlyStatus ne 'Cancelled')&orderby=WorkOrderCode asc,WorkOrderSequence asc,Position asc`;

      if (
        this.SelectedScheduleTab === 0 &&
        !this.IsTimeLineView &&
        !this.IsCapacityView
      ) {
        filters = `(ProcessCode eq '${this.ProcessCode}') and ` + filters;
      }

      // construct odata urlExtension
      let odataUrlExtension = "";
      if (scheduleURL === "ProductionScheduleWorkOrder") {
        odataUrlExtension = this.filterValues
          ? `$expand=SalesOrderProducts&filter=((${this.filterValues}) or (WOType ne 'Production' and WOType ne 'Maintenance')) and ${filters}` // always show downtime slots regardless of filter (FriendlyStatus eq 'Active')
          : `$expand=SalesOrderProducts&filter=${filters}`;
      } else {
        odataUrlExtension = this.filterValues
          ? `&filter=((${this.filterValues}) or (WOType ne 'Production' and WOType ne 'Maintenance')) and ${filters}` // always show downtime slots regardless of filter (FriendlyStatus eq 'Active')
          : `&filter=${filters}`;
      }

      let dataManager = new DataManager({
        url:
          appConfig.VUE_APP_ROOT_API +
          `/api/${scheduleURL}?${odataUrlExtension}`,
        adaptor:
          this.SelectedScheduleTab === 0
            ? new WorkOrderAdaptor()
            : new LinkSOAdaptor(),
        crossDomain: true,
      });

      dataManager.adaptor.beforeSend = function (dm, request) {
        request.setRequestHeader("x-tenant-id", TokenStorage.getTenant());
        request.setRequestHeader(
          "Authorization",
          `Bearer ${TokenStorage.getToken()}`,
        );
      };

      return dataManager;
    },

    getSchedulerReady() {
      return this.schedulerReady;
    },

    /**
     * Retun filter panel form items
     */
    filterFormItems() {
      return [
        {
          screenField: "Code",
          filterLabel: "Work Order",
          filterDataField: "WorkOrderCode",
          type: "Integer",
        },
        {
          screenField: "SalesOrder",
          gridDataField: "SalesOrderList",
          dataField: "SalesOrderList",
          filterDataField: "SalesOrderList",
          type: "Text",
        },
        {
          screenField: "Product",
          filterDataField: "ProductCode",

          filterOverWrites: {
            select: ["Code", "Description1", "Inactive", "Status", "Source"],
            filter: [
              ["Inactive", "=", "N"],
              "and",
              ["Status", "=", "A"],
              "and",
              ["Source", "=", "M"],
            ],
          },
        },
        {
          screenField: "BOM",
          filterDataField: "BomCode",
        },
        {
          screenField: "Status",
          filterDataField: "FriendlyStatus",
          dataSourceConfig: {
            endpoint: "api/WorkOrderStatus",
            sort: {
              selector: "Sequence",
              asc: true,
            },
            filter: [["Inactive", "=", "N"], "and", ["Code", "<>", "X"]],
            select: ["FriendlyStatus", "Sequence", "Inactive", "Code"],
          },
          editorTemplate: {
            primary: [
              {
                dataField: "FriendlyStatus",
              },
            ],
          },
          valueExpr: "FriendlyStatus",
          displayExpr: "FriendlyStatus",
        },
      ];
    },
    /**
     * return tab value
     */
    getProcessTabs() {
      return this.processTabs;
    },

    getTimeLineTabs() {
      return [
        {
          id: 0,
          code: "capacity",
          text: "Capacity",
          applicationFeatureCode: "ProductionSchedule",
          onClick: this.onTimelineTabChanged,
        },
        {
          id: this.processTabs.length + 1,
          code: "timeline",
          text: "Timeline",
          applicationFeatureCode: "ProductionSchedule",
          onClick: this.onProcessChanged,
        },
      ].concat(this.processTabs);
    },

    /**
     * return selected tab index
     */
    selectedTabIndex() {
      return this.selectedIndex;
    },

    getAddNewItems() {
      return [
        {
          text: "Work Order",
          value: "workorder",
          disabled: !this.getPermissionToCreate,
        },
        {
          text: "Maintenance Order",
          value: "maintenance",
          disabled: !FeatureStorage.checkPermission("MaintenanceOrder", "edit"),
        },
        {
          text: "Schedule Note",
          value: "note",
          disabled: !FeatureStorage.checkPermission(
            "ProductionSchedule",
            "edit",
          ),
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.production-calendar {
  .process-tabs {
    width: 100%;

    .dx-tabs-wrapper {
      display: flex;
      // div.dx-item.dx-tab:first-child {
      //   width: 150px;
      // }

      // div.dx-item.dx-tab:not(:first-child) {
      //   width: 150px;
      // }
    }
  }
  // .event-template {
  //   .description {
  //     text-wrap: wrap;
  //   }
  // }

  // .e-schedule .e-vertical-view.e-timescale-disable .e-appointment {
  //   height: auto !important;
  // }

  .e-schedule .e-month-view .e-current-date .e-date-header,
  .e-calendar .e-content td.e-selected span.e-day,
  .e-bigger.e-small .e-calendar .e-content td.e-selected span.e-day {
    background-color: #03a9f4 !important;
    border: 1px solid #03a9f4;
  }

  .e-btn.e-flat.e-primary,
  .e-css.e-btn.e-flat.e-primary,
  .e-active-view .e-tbar-btn-text,
  .e-current-day {
    color: #03a9f4 !important;
  }

  .e-schedule
    .e-vertical-view
    .e-day-wrapper
    .e-appointment
    .e-appointment-details {
    height: 100% !important;
    padding: 0px !important;
  }

  .e-schedule .e-vertical-view .e-work-cells {
    background: #f5f5f5;
  }

  .e-schedule .e-vertical-view .e-work-cells:hover {
    background: #ededed;
  }

  .e-schedule .e-vertical-view .e-work-hours {
    background: #ffffff;
  }

  .e-schedule .e-vertical-view .e-clone-time-indicator,
  .e-schedule .e-vertical-view .e-current-time {
    color: #03a9f4 !important;
  }

  .e-schedule .e-vertical-view .e-previous-timeline,
  .e-schedule .e-vertical-view .e-current-timeline {
    border-color: #03a9f4 !important;
  }

  .e-schedule .e-month-view .e-appointment,
  .e-schedule .e-vertical-view.e-timescale-disable .e-appointment {
    // background-color: #03a9f4 !important;

    background-color: unset;
    margin-bottom: 10px;

    .e-appointment-details {
      padding: unset;
      width: inherit;

      > div {
        width: 100%;
      }
    }
  }

  .e-schedule .e-month-view .e-appointment {
    height: 40px !important;

    .e-appointment-details {
      line-height: unset !important;
    }
  }

  .active {
    background: #62d26f;
  }

  .inproduction {
    background-color: #ffa500;
  }

  .completed {
    background-color: #4186e0;
  }

  .cancelled {
    background-color: #e8384f;
  }

  .e-block-appointment {
    display: flex;
    justify-content: center; /* horizontal center */
    align-items: center; /* vertical center */
    .template-wrap.downTime {
      span {
        color: #ffffff;
      }
    }
    z-index: 1;
  }

  .downTime {
    // background-color: gray;
    // padding: 2px;
  }

  .e-more-popup-wrapper.e-popup.e-popup-open {
    .e-appointment {
      background: unset !important;
      line-height: unset !important;
    }
  }

  .e-content-wrap {
    width: 100%;
    overflow-y: auto;
    // overflow-x: hidden;
    box-sizing: border-box;
    .e-appointment-wrapper {
      width: 100%;
      .e-appointment {
        // width: 100% !important;
      }
      .e-appointment.set-width {
        // padding-right: 10px;
      }
    }
  }

  .e-table-wrap.e-week-view,
  .e-table-wrap.e-work-week-view,
  .e-table-wrap.e-day-view {
    .e-schedule-table.e-content-table {
      .e-appointment.e-lib.e-draggable {
        // max-width: 100% !important;
        padding-left: 1px;
        // padding-right: 2px;
        background: transparent;
        border-left: unset;
        border-right: unset;
      }
    }
  }

  // .e-schedule-table.e-content-table {
  //   td {
  //     border-color: rgba(0, 0, 0, 0.12) !important;
  //     border-style: solid !important;
  //     border-width: 0 0 1px 1px !important;
  //   }
  // }

  .e-schedule .e-work-cells:not(.e-work-hours) {
    background: rgba(0, 0, 0, 0.04);
  }
}

.production-calendar.is-linked-sales-orders {
  .e-schedule .e-vertical-view.e-timescale-disable .e-appointment {
    height: 88px !important;
  }
}

.capacity-heatmap-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
}
</style>
